import { Text } from '@cyberpion/cyberpion-ui';
import { IReduxState } from 'app/store';
import classNames from 'classnames';
import { DropdownWithAction } from 'common/components/DropdownWithAction/DropdownWithAction';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useSelector } from 'react-redux';

import './PopSideTabs.scss';

const iconDetails = (color: string) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M13 13.8536V5.5L9.50017 2H4V11.5V13.5C4 13.6326 4.05268 13.7598 4.14644 13.8536C4.2402 13.9473 4.36737 14 4.49998 14H13"
      stroke={color}
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path d="M9.5 2V5.5H13" stroke={color} stroke-linecap="round" stroke-linejoin="round" />
    <path d="M10.5 8.5H6.5" stroke={color} stroke-linecap="round" stroke-linejoin="round" />
    <path d="M8.5 10.5H6.5" stroke={color} stroke-linecap="round" stroke-linejoin="round" />
  </svg>
);

const iconCVEs = (color: string) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M2.23398 12.763L7.55976 2.87221C7.74855 2.52161 8.25145 2.52161 8.44023 2.87221L13.766 12.7629C13.9454 13.0961 13.7041 13.5 13.3258 13.5L2.67422 13.5C2.29588 13.5 2.05461 13.0961 2.23398 12.763Z"
      stroke={color}
    />
    <path d="M8 6.5V9.5" stroke={color} />
    <path d="M8 10.5V11.5" stroke={color} />
  </svg>
);

const iconComment = (color: string) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M4.49003 12.8668C5.47696 13.5798 6.68941 14 8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 9.2893 2.40666 10.4836 3.09871 11.4617L2.70601 13.2948L4.49003 12.8668ZM2.03316 11.6618C1.37798 10.5961 1 9.34099 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8C15 11.866 11.866 15 8 15C6.64169 15 5.37162 14.6122 4.2972 13.9414L1.39566 14.6377L2.03316 11.6618Z"
      fill={color}
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M11.5 7C11.5 6.72386 11.2761 6.5 11 6.5H6C5.72386 6.5 5.5 6.72386 5.5 7C5.5 7.27614 5.72386 7.5 6 7.5H11C11.2761 7.5 11.5 7.27614 11.5 7Z"
      fill={color}
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M9.5 9C9.5 8.72386 9.27614 8.5 9 8.5H6C5.72386 8.5 5.5 8.72386 5.5 9C5.5 9.27614 5.72386 9.5 6 9.5H9C9.27614 9.5 9.5 9.27614 9.5 9Z"
      fill={color}
    />
  </svg>
);

const iconGraph = (color: string) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect
      x="8.05859"
      y="5.7657"
      width="3.36057"
      height="3.36057"
      rx="1.68028"
      transform="rotate(45 8.05859 5.7657)"
      stroke={color}
    />
    <line x1="8.18262" y1="3.1709" x2="8.18262" y2="6.34737" stroke={color} stroke-linecap="round" />
    <line x1="13.0293" y1="8.18262" x2="9.85283" y2="8.18262" stroke={color} stroke-linecap="round" />
    <path d="M11.9012 4.43292L9.35327 6.84739" stroke={color} stroke-linecap="round" />
    <rect
      x="0.5"
      y="-0.5"
      width="1.50588"
      height="1.50588"
      rx="0.752941"
      transform="matrix(1 0 0 -1 6.84668 2.50586)"
      stroke={color}
    />
    <rect
      x="13.4473"
      y="4.25879"
      width="1.50588"
      height="1.50588"
      rx="0.752941"
      transform="rotate(-180 13.4473 4.25879)"
      stroke={color}
    />
    <rect
      x="-0.5"
      y="0.5"
      width="1.50588"
      height="1.50588"
      rx="0.752941"
      transform="matrix(-1 0 0 1 14.2002 6.84668)"
      stroke={color}
    />
    <line
      x1="0.5"
      y1="-0.5"
      x2="3.67647"
      y2="-0.5"
      transform="matrix(4.37114e-08 -1 -1 -4.37114e-08 7.68262 13.5293)"
      stroke={color}
      stroke-linecap="round"
    />
    <path d="M11.9012 11.7673L9.35327 9.3528" stroke={color} stroke-linecap="round" />
    <rect x="7.34668" y="13.1943" width="1.50588" height="1.50588" rx="0.752941" stroke={color} />
    <rect
      x="-0.5"
      y="0.5"
      width="1.50588"
      height="1.50588"
      rx="0.752941"
      transform="matrix(-1 0 0 1 12.9473 11.4414)"
      stroke={color}
    />
    <line
      x1="0.5"
      y1="-0.5"
      x2="3.67647"
      y2="-0.5"
      transform="matrix(1 0 0 -1 2.6709 7.68262)"
      stroke={color}
      stroke-linecap="round"
    />
    <path d="M4.29898 4.43292L6.84693 6.84739" stroke={color} stroke-linecap="round" />
    <rect
      x="0.5"
      y="-0.5"
      width="1.50588"
      height="1.50588"
      rx="0.752941"
      transform="matrix(1 0 0 -1 2.25293 3.75879)"
      stroke={color}
    />
    <rect x="1.5" y="7.34668" width="1.50588" height="1.50588" rx="0.752941" stroke={color} />
    <path d="M4.29898 11.7673L6.84693 9.3528" stroke={color} stroke-linecap="round" />
    <rect x="2.75293" y="11.9414" width="1.50588" height="1.50588" rx="0.752941" stroke={color} />
  </svg>
);

const iconAiSummary = (isSelected: boolean) => {
  return isSelected ? (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
      <path
        d="M10.9998 1.84492V4.84492M13.9998 5.34492V7.34492M9.49984 3.34492H12.4998M12.9998 6.34492H14.9998M5.26672 11.578L1.82359 10.3093C1.72865 10.2743 1.64673 10.211 1.58888 10.1279C1.53102 10.0449 1.5 9.94613 1.5 9.84492C1.5 9.74372 1.53102 9.64496 1.58888 9.56192C1.64673 9.47889 1.72865 9.41559 1.82359 9.38055L5.26672 8.1118L6.53547 4.66867C6.57051 4.57374 6.63381 4.49182 6.71684 4.43396C6.79987 4.3761 6.89864 4.34508 6.99984 4.34508C7.10104 4.34508 7.19981 4.3761 7.28284 4.43396C7.36587 4.49182 7.42917 4.57374 7.46422 4.66867L8.73297 8.1118L12.1761 9.38055C12.271 9.41559 12.3529 9.47889 12.4108 9.56192C12.4687 9.64496 12.4997 9.74372 12.4997 9.84492C12.4997 9.94613 12.4687 10.0449 12.4108 10.1279C12.3529 10.211 12.271 10.2743 12.1761 10.3093L8.73297 11.578L7.46422 15.0212C7.42917 15.1161 7.36587 15.198 7.28284 15.2559C7.19981 15.3137 7.10104 15.3448 6.99984 15.3448C6.89864 15.3448 6.79987 15.3137 6.71684 15.2559C6.63381 15.198 6.57051 15.1161 6.53547 15.0212L5.26672 11.578Z"
        stroke="#3455DD"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  ) : (
    <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M10.9998 1.84492V4.84492M13.9998 5.34492V7.34492M9.49984 3.34492H12.4998M12.9998 6.34492H14.9998M5.26672 11.578L1.82359 10.3093C1.72865 10.2743 1.64673 10.211 1.58888 10.1279C1.53102 10.0449 1.5 9.94613 1.5 9.84492C1.5 9.74372 1.53102 9.64496 1.58888 9.56192C1.64673 9.47889 1.72865 9.41559 1.82359 9.38055L5.26672 8.1118L6.53547 4.66867C6.57051 4.57374 6.63381 4.49182 6.71684 4.43396C6.79987 4.3761 6.89864 4.34508 6.99984 4.34508C7.10104 4.34508 7.19981 4.3761 7.28284 4.43396C7.36587 4.49182 7.42917 4.57374 7.46422 4.66867L8.73297 8.1118L12.1761 9.38055C12.271 9.41559 12.3529 9.47889 12.4108 9.56192C12.4687 9.64496 12.4997 9.74372 12.4997 9.84492C12.4997 9.94613 12.4687 10.0449 12.4108 10.1279C12.3529 10.211 12.271 10.2743 12.1761 10.3093L8.73297 11.578L7.46422 15.0212C7.42917 15.1161 7.36587 15.198 7.28284 15.2559C7.19981 15.3137 7.10104 15.3448 6.99984 15.3448C6.89864 15.3448 6.79987 15.3137 6.71684 15.2559C6.63381 15.198 6.57051 15.1161 6.53547 15.0212L5.26672 11.578Z"
        stroke="#5D6576"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
};

const iconBeta = (isSelected: boolean) => {
  return isSelected ? (
    <svg width="32" height="18" viewBox="0 0 32 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g id="Frame 2095586372">
        <rect x="0.5" y="1" width="31" height="16" rx="8" stroke="url(#paint0_linear_211_57697)" />
        <path
          id="BETA"
          d="M6.88 11.5C6.82133 11.5 6.776 11.484 6.744 11.452C6.712 11.4147 6.696 11.3693 6.696 11.316V6.092C6.696 6.03333 6.712 5.988 6.744 5.956C6.776 5.91867 6.82133 5.9 6.88 5.9H9.072C9.472 5.9 9.79733 5.96667 10.048 6.1C10.304 6.228 10.4907 6.404 10.608 6.628C10.7307 6.852 10.792 7.10267 10.792 7.38C10.792 7.59867 10.7547 7.788 10.68 7.948C10.6107 8.10267 10.5227 8.23067 10.416 8.332C10.3093 8.428 10.2053 8.50267 10.104 8.556C10.312 8.65733 10.496 8.82267 10.656 9.052C10.8213 9.28133 10.904 9.55867 10.904 9.884C10.904 10.1773 10.8373 10.4467 10.704 10.692C10.5707 10.9373 10.3707 11.1347 10.104 11.284C9.84267 11.428 9.51733 11.5 9.128 11.5H6.88ZM7.48 10.852H9.024C9.37067 10.852 9.63733 10.7587 9.824 10.572C10.0107 10.3853 10.104 10.156 10.104 9.884C10.104 9.60133 10.0107 9.36933 9.824 9.188C9.63733 9.00133 9.37067 8.908 9.024 8.908H7.48V10.852ZM7.48 8.268H8.952C9.29333 8.268 9.552 8.19067 9.728 8.036C9.904 7.876 9.992 7.66 9.992 7.388C9.992 7.116 9.904 6.908 9.728 6.764C9.552 6.62 9.29333 6.548 8.952 6.548H7.48V8.268ZM12.2238 11.5C12.1651 11.5 12.1198 11.484 12.0878 11.452C12.0558 11.4147 12.0398 11.3693 12.0398 11.316V6.092C12.0398 6.03333 12.0558 5.988 12.0878 5.956C12.1198 5.91867 12.1651 5.9 12.2238 5.9H15.5278C15.5864 5.9 15.6318 5.91867 15.6638 5.956C15.6958 5.988 15.7118 6.03333 15.7118 6.092V6.396C15.7118 6.45467 15.6958 6.5 15.6638 6.532C15.6318 6.564 15.5864 6.58 15.5278 6.58H12.8078V8.332H15.3518C15.4104 8.332 15.4558 8.35067 15.4878 8.388C15.5198 8.42 15.5358 8.46533 15.5358 8.524V8.82C15.5358 8.87333 15.5198 8.91867 15.4878 8.956C15.4558 8.988 15.4104 9.004 15.3518 9.004H12.8078V10.82H15.5918C15.6504 10.82 15.6958 10.836 15.7278 10.868C15.7598 10.9 15.7758 10.9453 15.7758 11.004V11.316C15.7758 11.3693 15.7598 11.4147 15.7278 11.452C15.6958 11.484 15.6504 11.5 15.5918 11.5H12.2238ZM18.2993 11.5C18.246 11.5 18.2006 11.484 18.1633 11.452C18.1313 11.4147 18.1153 11.3693 18.1153 11.316V6.612H16.5793C16.526 6.612 16.4806 6.596 16.4433 6.564C16.4113 6.52667 16.3953 6.48133 16.3953 6.428V6.092C16.3953 6.03333 16.4113 5.988 16.4433 5.956C16.4806 5.91867 16.526 5.9 16.5793 5.9H20.4353C20.494 5.9 20.5393 5.91867 20.5713 5.956C20.6086 5.988 20.6273 6.03333 20.6273 6.092V6.428C20.6273 6.48133 20.6086 6.52667 20.5713 6.564C20.5393 6.596 20.494 6.612 20.4353 6.612H18.9073V11.316C18.9073 11.3693 18.8886 11.4147 18.8513 11.452C18.8193 11.484 18.774 11.5 18.7153 11.5H18.2993ZM20.5544 11.5C20.5117 11.5 20.4744 11.484 20.4424 11.452C20.4104 11.42 20.3944 11.3827 20.3944 11.34C20.3944 11.3133 20.397 11.284 20.4024 11.252L22.3864 6.076C22.4077 6.02267 22.437 5.98 22.4744 5.948C22.5117 5.916 22.5677 5.9 22.6424 5.9H23.1864C23.2557 5.9 23.309 5.916 23.3464 5.948C23.389 5.98 23.421 6.02267 23.4424 6.076L25.4184 11.252C25.429 11.284 25.4344 11.3133 25.4344 11.34C25.4344 11.3827 25.4184 11.42 25.3864 11.452C25.3544 11.484 25.317 11.5 25.2744 11.5H24.8664C24.8024 11.5 24.7544 11.484 24.7224 11.452C24.6957 11.42 24.677 11.3907 24.6664 11.364L24.2264 10.228H21.6024L21.1624 11.364C21.157 11.3907 21.1384 11.42 21.1064 11.452C21.0744 11.484 21.0264 11.5 20.9624 11.5H20.5544ZM21.8344 9.54H23.9944L22.9144 6.7L21.8344 9.54Z"
          fill="#8796F0"
        />
      </g>
      <defs>
        <linearGradient id="paint0_linear_211_57697" x1="0" y1="9" x2="32" y2="9" gradientUnits="userSpaceOnUse">
          <stop stop-color="#AEB8F5" />
          <stop offset="1" stop-color="#909EF1" />
        </linearGradient>
      </defs>
    </svg>
  ) : (
    <svg width="32" height="18" viewBox="0 0 32 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g id="Frame 2095586372">
        <rect x="0.5" y="1" width="31" height="16" rx="8" stroke="url(#paint0_linear_211_57697)" />
        <path
          id="BETA"
          d="M6.88 11.5C6.82133 11.5 6.776 11.484 6.744 11.452C6.712 11.4147 6.696 11.3693 6.696 11.316V6.092C6.696 6.03333 6.712 5.988 6.744 5.956C6.776 5.91867 6.82133 5.9 6.88 5.9H9.072C9.472 5.9 9.79733 5.96667 10.048 6.1C10.304 6.228 10.4907 6.404 10.608 6.628C10.7307 6.852 10.792 7.10267 10.792 7.38C10.792 7.59867 10.7547 7.788 10.68 7.948C10.6107 8.10267 10.5227 8.23067 10.416 8.332C10.3093 8.428 10.2053 8.50267 10.104 8.556C10.312 8.65733 10.496 8.82267 10.656 9.052C10.8213 9.28133 10.904 9.55867 10.904 9.884C10.904 10.1773 10.8373 10.4467 10.704 10.692C10.5707 10.9373 10.3707 11.1347 10.104 11.284C9.84267 11.428 9.51733 11.5 9.128 11.5H6.88ZM7.48 10.852H9.024C9.37067 10.852 9.63733 10.7587 9.824 10.572C10.0107 10.3853 10.104 10.156 10.104 9.884C10.104 9.60133 10.0107 9.36933 9.824 9.188C9.63733 9.00133 9.37067 8.908 9.024 8.908H7.48V10.852ZM7.48 8.268H8.952C9.29333 8.268 9.552 8.19067 9.728 8.036C9.904 7.876 9.992 7.66 9.992 7.388C9.992 7.116 9.904 6.908 9.728 6.764C9.552 6.62 9.29333 6.548 8.952 6.548H7.48V8.268ZM12.2238 11.5C12.1651 11.5 12.1198 11.484 12.0878 11.452C12.0558 11.4147 12.0398 11.3693 12.0398 11.316V6.092C12.0398 6.03333 12.0558 5.988 12.0878 5.956C12.1198 5.91867 12.1651 5.9 12.2238 5.9H15.5278C15.5864 5.9 15.6318 5.91867 15.6638 5.956C15.6958 5.988 15.7118 6.03333 15.7118 6.092V6.396C15.7118 6.45467 15.6958 6.5 15.6638 6.532C15.6318 6.564 15.5864 6.58 15.5278 6.58H12.8078V8.332H15.3518C15.4104 8.332 15.4558 8.35067 15.4878 8.388C15.5198 8.42 15.5358 8.46533 15.5358 8.524V8.82C15.5358 8.87333 15.5198 8.91867 15.4878 8.956C15.4558 8.988 15.4104 9.004 15.3518 9.004H12.8078V10.82H15.5918C15.6504 10.82 15.6958 10.836 15.7278 10.868C15.7598 10.9 15.7758 10.9453 15.7758 11.004V11.316C15.7758 11.3693 15.7598 11.4147 15.7278 11.452C15.6958 11.484 15.6504 11.5 15.5918 11.5H12.2238ZM18.2993 11.5C18.246 11.5 18.2006 11.484 18.1633 11.452C18.1313 11.4147 18.1153 11.3693 18.1153 11.316V6.612H16.5793C16.526 6.612 16.4806 6.596 16.4433 6.564C16.4113 6.52667 16.3953 6.48133 16.3953 6.428V6.092C16.3953 6.03333 16.4113 5.988 16.4433 5.956C16.4806 5.91867 16.526 5.9 16.5793 5.9H20.4353C20.494 5.9 20.5393 5.91867 20.5713 5.956C20.6086 5.988 20.6273 6.03333 20.6273 6.092V6.428C20.6273 6.48133 20.6086 6.52667 20.5713 6.564C20.5393 6.596 20.494 6.612 20.4353 6.612H18.9073V11.316C18.9073 11.3693 18.8886 11.4147 18.8513 11.452C18.8193 11.484 18.774 11.5 18.7153 11.5H18.2993ZM20.5544 11.5C20.5117 11.5 20.4744 11.484 20.4424 11.452C20.4104 11.42 20.3944 11.3827 20.3944 11.34C20.3944 11.3133 20.397 11.284 20.4024 11.252L22.3864 6.076C22.4077 6.02267 22.437 5.98 22.4744 5.948C22.5117 5.916 22.5677 5.9 22.6424 5.9H23.1864C23.2557 5.9 23.309 5.916 23.3464 5.948C23.389 5.98 23.421 6.02267 23.4424 6.076L25.4184 11.252C25.429 11.284 25.4344 11.3133 25.4344 11.34C25.4344 11.3827 25.4184 11.42 25.3864 11.452C25.3544 11.484 25.317 11.5 25.2744 11.5H24.8664C24.8024 11.5 24.7544 11.484 24.7224 11.452C24.6957 11.42 24.677 11.3907 24.6664 11.364L24.2264 10.228H21.6024L21.1624 11.364C21.157 11.3907 21.1384 11.42 21.1064 11.452C21.0744 11.484 21.0264 11.5 20.9624 11.5H20.5544ZM21.8344 9.54H23.9944L22.9144 6.7L21.8344 9.54Z"
          fill="#5D657690"
        />
      </g>
      <defs>
        <linearGradient id="paint0_linear_211_57697" x1="0" y1="9" x2="32" y2="9" gradientUnits="userSpaceOnUse">
          <stop stop-color="#5D657690" />
          <stop offset="1" stop-color="#5D657690" />
        </linearGradient>
      </defs>
    </svg>
  );
};

interface IPopSideTabs {
  data: any;
  tabActive: string;
  setTabActive: (tab: string) => void;
}
const PopSideTabs: React.FC<IPopSideTabs> = ({ data, tabActive, setTabActive }) => {
  // const { user } = useSelector((state: IReduxState) => state.login);
  const flags = useFlags();
  const { commentsTabCount } = useSelector((state: IReduxState) => state.popSide);
  const showSummaryTab = !!flags?.shouldShowAi;

  return (
    <div className="PopSideTabs">
      <div className={classNames('ai-popside-tab')} style={{ marginRight: 8 }} onClick={() => setTabActive('details')}>
        <div className={classNames('icon', { active: tabActive === 'details' })}>
          {iconDetails(tabActive === 'details' ? '#3455DD' : '#6D6D7A')}
        </div>
        <Text textSize={14} textColor={tabActive === 'details' ? '#3455DD' : '#6D6D7A'} weight={500}>
          Details
        </Text>
        {/* <div style={{ height: 1, width: '100%', backgroundColor: '#3455DD' }}></div> */}
      </div>
      {!!data?.cves?.length && (
        <div className={classNames('ai-popside-tab')} style={{ margin: '0 8px' }} onClick={() => setTabActive('cves')}>
          <div className={classNames('icon', { active: tabActive === 'cves' })}>
            {iconCVEs(tabActive === 'cves' ? '#3455DD' : '#6D6D7A')}
          </div>
          <Text textSize={14} textColor={tabActive === 'cves' ? '#3455DD' : '#6D6D7A'} weight={500}>{`CVEs (${data?.cves
            ?.length || 0})`}</Text>
        </div>
      )}
      <div
        className={classNames('ai-popside-tab')}
        style={{ margin: '0 8px' }}
        onClick={() => setTabActive('comments')}
      >
        <div className={classNames('icon', { active: tabActive === 'comments' })}>
          {iconComment(tabActive === 'comments' ? '#3455DD' : '#6D6D7A')}
        </div>
        <Text textSize={14} textColor={tabActive === 'comments' ? '#3455DD' : '#6D6D7A'} weight={500}>
          {!!commentsTabCount ? `Comments (${commentsTabCount})` : 'Comments'}
        </Text>
      </div>
      {flags.isGraphTab && (
        <div className={classNames('ai-popside-tab')} onClick={() => setTabActive('graph')}>
          <div className={classNames('icon', { active: tabActive === 'graph' })}>
            {iconGraph(tabActive === 'graph' ? '#3455DD' : '#6D6D7A')}
          </div>

          <Text textSize={14} textColor={tabActive === 'graph' ? '#3455DD' : '#6D6D7A'} weight={500}>
            Attack Surface Graph
          </Text>
        </div>
      )}
      {showSummaryTab && (
        <div
          className={classNames('ai-popside-tab')}
          style={{ paddingLeft: 8 }}
          onClick={() => setTabActive('ionixAiSummary')}
        >
          <div className={classNames('icon', { active: tabActive === 'ionixAiSummary' })}>
            {iconAiSummary(tabActive === 'ionixAiSummary')}
          </div>
          <Text textSize={14} textColor={tabActive === 'ionixAiSummary' ? '#3455DD' : '#6D6D7A'} weight={500}>
            IONIX AI Summary
          </Text>
          <div style={{ position: 'relative', top: 2, marginLeft: 4 }}>{iconBeta(tabActive === 'ionixAiSummary')}</div>
        </div>
      )}
      {!data.last_closed_at && (
        <>
          <div style={{ flexGrow: 1, paddingRight: 8 }} />
          <DropdownWithAction row={data} />
        </>
      )}
    </div>
  );
};

export default PopSideTabs;
